import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Alert, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Grid, Chip, FormControlLabel, Switch, CircularProgress, useTheme,
} from '@mui/material';
import { API_ENDPOINTS } from '../../config/api';
import { TOOL_COLORS } from '../../constants/colors';

const ACCENT = TOOL_COLORS.OTHER.primary;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('jwtToken')}` });

/**
 * Change the defaults a report's parameters start from.
 *
 * This works on any report, coded ones included — "make the row limit 500"
 * should not need a deploy. What it cannot do is add or remove a parameter on a
 * coded report: the SQL decides which exist, and one the query never mentions
 * would be a box on a form that changes nothing. To add or remove parameters,
 * the report has to be one built here, edited through the builder.
 */
const ParameterDefaultsDialog = ({ open, reportKey, onClose, onSaved, notify }) => {
  const theme = useTheme();
  const [info, setInfo] = useState(null);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!reportKey) return;
    setBusy(true);
    try {
      const r = await fetch(API_ENDPOINTS.REPORTS_SCHEDULER_CUSTOM_PARAMS(reportKey), { headers: authHeaders() });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || `${r.status}`);
      setInfo(d);
      setRows((d.parameters || []).map((p) => ({ ...p })));
    } catch (e) { notify(e.message, 'error'); } finally { setBusy(false); }
  }, [reportKey, notify]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const save = async () => {
    setBusy(true);
    try {
      // Only send what actually differs from the coded value; an unchanged row
      // should leave no trace, so "has this been customised?" stays honest.
      const body = rows
        .filter((p) => (p.default ?? null) !== (p.codedDefault ?? null)
                    || p.required !== p.codedRequired)
        .map((p) => ({
          reportKey,
          parameter: p.name,
          default: p.default ?? null,
          required: p.required === p.codedRequired ? null : p.required,
          description: null,
        }));
      const r = await fetch(API_ENDPOINTS.REPORTS_SCHEDULER_CUSTOM_PARAMS(reportKey), {
        method: 'PUT',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || `${r.status}`);
      setInfo(d);
      setRows((d.parameters || []).map((p) => ({ ...p })));
      notify('Defaults saved', 'success');
      onSaved();
    } catch (e) { notify(e.message, 'error'); } finally { setBusy(false); }
  };

  const reset = async () => {
    setBusy(true);
    try {
      const r = await fetch(API_ENDPOINTS.REPORTS_SCHEDULER_CUSTOM_PARAMS(reportKey), {
        method: 'DELETE', headers: authHeaders(),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || `${r.status}`);
      setInfo(d);
      setRows((d.parameters || []).map((p) => ({ ...p })));
      notify('Back to the coded defaults', 'success');
      onSaved();
    } catch (e) { notify(e.message, 'error'); } finally { setBusy(false); }
  };

  const setRow = (i, patch) => setRows((prev) => {
    const next = [...prev];
    next[i] = { ...next[i], ...patch };
    return next;
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Parameters{info ? ` — ${info.name}` : ''}
        {info && info.builtIn && <Chip size="small" label="built-in" variant="outlined" sx={{ ml: 1 }} />}
      </DialogTitle>
      <DialogContent dividers>
        {busy && !info && <CircularProgress size={24} />}

        {info && info.builtIn && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="caption">
              This report&apos;s query is part of the application, so its parameters cannot be added to or
              removed here — only their starting values changed. Those changes apply to every run and
              every schedule immediately.
            </Typography>
          </Alert>
        )}

        {info && (info.parameters || []).length === 0 && (
          <Typography variant="body2">This report takes no parameters.</Typography>
        )}

        {rows.map((p, i) => (
          <Box key={p.name} sx={{ mb: 2 }}>
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={12} sm={3}>
                <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>{p.name}</Typography>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>{p.type}</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Default" size="small" fullWidth value={p.default ?? ''}
                  onChange={(e) => setRow(i, { default: e.target.value })}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <FormControlLabel
                  control={<Switch size="small" checked={!!p.required} onChange={(e) => setRow(i, { required: e.target.checked })} />}
                  label={<Typography variant="caption">Required</Typography>}
                />
              </Grid>
              <Grid item xs={6} sm={2}>
                {p.overridden && <Chip size="small" color="primary" variant="outlined" label="changed" />}
              </Grid>
              {p.description && (
                <Grid item xs={12}>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>{p.description}</Typography>
                </Grid>
              )}
              {(p.codedDefault ?? null) !== (p.default ?? null) && (
                <Grid item xs={12}>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                    Coded default: {p.codedDefault === null || p.codedDefault === undefined ? 'none' : `"${p.codedDefault}"`}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Box>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={reset} disabled={busy} sx={{ mr: 'auto' }}>Reset to coded defaults</Button>
        <Button onClick={onClose} disabled={busy}>Close</Button>
        <Button
          variant="contained" onClick={save} disabled={busy || !info || (info.parameters || []).length === 0}
          sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: TOOL_COLORS.OTHER.hover } }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ParameterDefaultsDialog;
